import { useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, Overline } from "./Reveal";

export function Testimonials({ reviews, summary }) {
  const [emblaRef, embla] = useEmblaCarousel({ align: "start", loop: true, dragFree: false });
  const scrollPrev = useCallback(() => embla && embla.scrollPrev(), [embla]);
  const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla]);

  if (!reviews?.length) return null;

  return (
    <section id="reviews" className="px-5 py-16 bg-secondary/50 scroll-mt-16">
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <div className="flex items-end justify-between gap-4">
            <div>
              <Overline>Loved in Kanpur</Overline>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-primary mt-3">What guests say</h2>
            </div>
            <div className="hidden sm:flex gap-2">
              <button data-testid="reviews-prev" onClick={scrollPrev} aria-label="Previous"
                className="grid place-items-center w-11 h-11 rounded-full border border-primary text-primary active:scale-95 transition-transform">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button data-testid="reviews-next" onClick={scrollNext} aria-label="Next"
                className="grid place-items-center w-11 h-11 rounded-full border border-primary text-primary active:scale-95 transition-transform">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </Reveal>

        <div className="mt-8 overflow-hidden" ref={emblaRef} data-testid="reviews-carousel">
          <div className="flex gap-4 sm:gap-5">
            {reviews.map((r) => (
              <div
                key={r.id}
                data-testid={`review-${r.id}`}
                className="shrink-0 w-[85%] sm:w-[46%] lg:w-[31%] rounded-3xl border border-border bg-card p-6 sm:p-7"
              >
                <Quote className="w-8 h-8 text-accent" />
                <div className="mt-3 flex gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className={`w-4 h-4 ${i < r.rating ? "text-accent fill-current" : "text-border"}`} />
                  ))}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-foreground/85">“{r.text}”</p>
                <div className="mt-5 pt-4 border-t border-border">
                  <div className="font-medium text-primary">{r.name}</div>
                  {r.event && <div className="text-xs text-muted-foreground mt-0.5">{r.event}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="mt-4 text-xs text-muted-foreground sm:hidden">← Swipe to see more reviews</p>

        {summary?.review_link && (
          <Reveal delay={0.1}>
            <a
              data-testid="leave-review-btn"
              href={summary.review_link}
              target="_blank"
              rel="noreferrer"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 h-12 font-medium active:scale-[0.98] transition-transform"
            >
              <Star className="w-4 h-4 fill-current text-accent" /> Leave us a Google review
            </a>
          </Reveal>
        )}
      </div>
    </section>
  );
}
